/**
 * Every state the builder can be in that is not a week: loading, a failed read, a plan this app
 * refuses to open, a sequential plan, and a plan with no phases.
 *
 * One shape five times, because they are one shape five times — a title, a sentence, and at most
 * one action. What differs is the WORDS, and the words matter: `CLAUDE.md` calls out that
 * collapsing distinguishable states into "nothing here" is the version that reads as a bug.
 *
 * Three of these deliberately offer **no retry**. A refusal, a sequential plan and an empty plan
 * are not transient — pressing a button would change nothing, and offering one tells the trainer
 * the app thinks it might work next time.
 */

import { useRef, type ReactNode, type RefObject } from 'react';

import { BrandLoader } from '~/shared/ui/BrandLoader';

import styles from './BuilderNotice.module.css';

interface BuilderNoticeProps {
  readonly title: string;
  readonly message?: string;
  readonly action?: { readonly label: string; readonly onClick: () => void };
  /** A way out that is a navigation rather than an action — the link back to the library. */
  readonly escape?: ReactNode;
  /**
   * Announce progress rather than a result. Only the loading state sets it, and since
   * 18 September 2026 it also chooses the drawing: the shared loader in place of the word, with
   * `title` as the sentence it carries for a screen reader. The box is unchanged — it is centred
   * in the work column either way — so the canvas arriving still moves nothing.
   */
  readonly busy?: boolean;
  /**
   * What stands between the sentence and the action — the restore question's plan card and its
   * two answers (`features/create-plan`'s `LeftoverPlanAnswers`). The notice keeps its chrome; a
   * feature supplies only what is its own.
   */
  readonly children?: ReactNode;
  /**
   * The heading, for a caller that has to put focus on it — an answer inside `children` that
   * unmounts itself, whose notice is about to say what happened.
   */
  readonly titleRef?: RefObject<HTMLHeadingElement | null>;
}

export function BuilderNotice({
  title,
  message,
  action,
  escape,
  busy,
  children,
  titleRef,
}: BuilderNoticeProps) {
  const notice = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={notice}
      className={styles.notice}
      // `status` rather than `alert`: every one of these replaces the main region on arrival, and
      // an assertive live region would interrupt whatever the screen reader was saying to repeat
      // text the user is already being moved to.
      role="status"
      aria-busy={busy === true ? true : undefined}
      // Focusable only as a focus TARGET, never a tab stop — see the retry handler below.
      tabIndex={-1}
    >
      {busy === true ? (
        <BrandLoader label={title} size="screen" />
      ) : (
        // A heading since 24 September 2026: each of these replaces the whole canvas, and the
        // restore question moves focus here once its own answer has removed itself.
        <h2 ref={titleRef} className={styles.title} tabIndex={-1}>
          {title}
        </h2>
      )}
      {message != null && <p className={styles.message}>{message}</p>}
      {children}
      {action != null && (
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            // Retry UNMOUNTS this button: the screen swaps to the loading notice while the read is
            // in flight. Focus would fall back to `<body>` and a keyboard user would restart from
            // the top of the document — so it is moved onto the notice, which is the element that
            // is about to carry the answer, before the state changes.
            notice.current?.focus();
            action.onClick();
          }}
        >
          {action.label}
        </button>
      )}
      {escape}
    </div>
  );
}
